import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import SettingsModal from './SettingsModal';

const AppHeader = () => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Check if there's calculation data available
  const hasCalculationData = () => {
    try {
      const savedLoanData = localStorage.getItem('currentLoanCalculation');
      return savedLoanData && JSON.parse(savedLoanData);
    } catch (error) {
      return false;
    }
  };

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/loan-calculator-dashboard',
      icon: 'Calculator'
    },
    {
      label: 'Print Preview',
      path: '/print-preview-report',
      icon: 'FileText',
      requiresData: true
    },
    {
      label: 'Settings',
      path: '/settings-configuration',
      icon: 'Settings'
    }
  ];

  const currentPage = navigationItems.find(item => item.path === location.pathname);
  const showActionToolbar = location.pathname === '/loan-calculator-dashboard';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.hamburger-menu-container')) {
        setIsMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleSettingsClick = () => {
    setIsSettingsOpen(true);
  };

  const handlePrintClick = () => {
    window.print();
  };

  const handleExportClick = () => {
    // Export functionality would be implemented here
    console.log('Export clicked');
  };

  const handlePrintPreviewClick = (item) => {
    if (item.requiresData && !hasCalculationData()) {
      // Navigate to print preview with sample parameter
      window.location.href = `${item.path}?sample=true`;
    } else {
      window.location.href = item.path;
    }
    setIsMenuOpen(false);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuItemClick = (item) => {
    if (item.requiresData) {
      handlePrintPreviewClick(item);
    } else {
      window.location.href = item.path;
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 shadow-soft border-b border-border/40">
        <div className="bg-gradient-to-r from-primary/5 via-white to-accent/5">
          <div className="flex items-center justify-between h-16 px-6">
            {/* Logo Section */}
            <div className="flex items-center">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl shadow-elevated">
                  <Icon name="Calculator" size={24} color="white" />
                </div>
                <div className="flex flex-col">
                  <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent leading-tight">
                    SBAS - AmortizationPro
                  </h1>
                  <span className="text-sm text-text-secondary leading-tight font-medium">
                    Professional Loan Calculator
                  </span>
                </div>
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-3">
              {/* Action Toolbar for Dashboard */}
              {showActionToolbar && (
                <div className="hidden sm:flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Printer"
                    iconPosition="left"
                    onClick={handlePrintClick}
                    className="btn-professional btn-outline hover:bg-primary/10 hover:text-primary hover:border-primary/30"
                  >
                    Print
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleExportClick}
                    className="btn-professional btn-outline hover:bg-accent/10 hover:text-accent hover:border-accent/30"
                  >
                    Export
                  </Button>
                </div>
              )}

              {/* Settings Button */}
              <Button
                variant="ghost"
                size="sm"
                iconName="Settings"
                onClick={handleSettingsClick}
                className="btn-professional btn-ghost text-text-secondary hover:text-primary hover:bg-primary/10 transition-all duration-300"
              >
                <span className="hidden sm:inline ml-2">Settings</span>
              </Button>

              {/* Hamburger Menu Button */}
              <div className="hamburger-menu-container relative">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Menu"
                  onClick={handleMenuToggle}
                  className="btn-professional btn-ghost text-text-secondary hover:text-primary hover:bg-primary/10 transition-all duration-300"
                >
                </Button>

                {/* Hamburger Menu Dropdown */}
                {isMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" />
                    
                    {/* Menu Panel */}
                    <div className="absolute right-0 top-12 w-72 bg-white rounded-xl shadow-elevated border border-border/40 z-50 overflow-hidden animate-fade-in">
                      <div className="py-2">
                        <div className="px-4 py-3 border-b border-border/40">
                          <h3 className="font-semibold text-text-primary">Navigation</h3>
                          <p className="text-sm text-text-secondary">Navigate to different sections</p>
                        </div>
                        
                        {navigationItems.map((item) => (
                          <button
                            key={item.path}
                            onClick={() => handleMenuItemClick(item)}
                            className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gradient-to-r hover:from-primary/10 hover:to-accent/10 transition-all duration-200 ${
                              location.pathname === item.path
                                ? 'bg-gradient-to-r from-primary/10 to-accent/10 text-primary border-r-2 border-primary' :'text-text-secondary hover:text-primary'
                            } ${item.requiresData && !hasCalculationData() ? 'relative' : ''}`}
                          >
                            <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                              location.pathname === item.path
                                ? 'bg-gradient-to-br from-primary to-accent text-white' :'bg-muted/50 text-text-secondary group-hover:bg-primary/10 group-hover:text-primary'
                            }`}>
                              <Icon name={item.icon} size={16} />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium">{item.label}</div>
                              <div className="text-xs text-text-secondary">
                                {item.path === '/loan-calculator-dashboard' && 'Calculate loan payments'}
                                {item.path === '/print-preview-report' && 'View and print reports'}
                                {item.path === '/settings-configuration' && 'Configure preferences'}
                              </div>
                            </div>
                            {item.requiresData && !hasCalculationData() && (
                              <div className="flex items-center space-x-1">
                                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                                <span className="text-xs text-amber-600">Sample</span>
                              </div>
                            )}
                            {location.pathname === item.path && (
                              <div className="text-primary">
                                <Icon name="Check" size={16} />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Settings Modal */}
      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />
    </>
  );
};

export default AppHeader;